/*
 * Annual Planner™ · Phase 2 · Strategic Planning Workspace
 * ---------------------------------------------------------------------
 * Governed by /app/memory/DESIGN_PRINCIPLES.md and shares the visual
 * language of SundayBuilder's Concept D. Backend-authoritative — the
 * frontend never fabricates dates, occasions, seasons, or season status.
 *
 *   GET /api/annual-planner/overview  →  { today, liturgical_year,
 *                                          milestone?, timeline, memory }
 *
 * One screen · one question · one primary action. The altar-frontal
 * band appears once as an architectural header. Every fact on-screen
 * traces back to the calendar or the parish's real data.
 * =================================================================== */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ChevronRight, Circle, CheckCircle2, Dot } from "lucide-react";
import "./sundaybuilder/workspace.css";
import "./annualplanner/workspace.css";
import useSocialMeta from "../hooks/useSocialMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ALTAR_URL = `${process.env.PUBLIC_URL || ""}/brand/altar-frontal.webp`;

const _MONTHS = ["January", "February", "March", "April", "May", "June",
                 "July", "August", "September", "October", "November", "December"];

function _monthDay(iso) {
  if (!iso) return "";
  const [, m, d] = iso.split("-").map(Number);
  return `${_MONTHS[m - 1]} ${d}`;
}

function _yearRangeLabel(bounds) {
  if (!bounds?.starts_iso || !bounds?.ends_iso) return "";
  const [ys] = bounds.starts_iso.split("-");
  const [ye] = bounds.ends_iso.split("-");
  return ys === ye ? ys : `${ys}–${ye}`;
}

function _statusIcon(status) {
  if (status === "past")    return <CheckCircle2 size={18} strokeWidth={1.75} aria-hidden="true" className="ap-status-icon ap-status-icon--past" />;
  if (status === "current") return <Dot size={30} strokeWidth={3} aria-hidden="true" className="ap-status-icon ap-status-icon--current" />;
  return <Circle size={18} strokeWidth={1.75} aria-hidden="true" className="ap-status-icon ap-status-icon--upcoming" />;
}

function _statusLabel(status) {
  if (status === "past")    return "Past";
  if (status === "current") return "In progress";
  return "Upcoming";
}

export default function AnnualPlanner() {
  useSocialMeta({
    title: "Annual Planner™ — See the whole year at a glance",
    description: "Plan every season before it arrives. The Annual Planner™ inside SundayBuilder™.",
    path: "/annual-planner",
  });
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    document.documentElement.style.setProperty("--li-altar-frontal-url", `url("${ALTAR_URL}")`);
    axios.get(`${API}/annual-planner/overview`)
      .then((r) => setOverview(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "The Annual Planner is temporarily unavailable."));
  }, []);

  const handleBeginPlanning = (seasonKey, startsIso) => {
    // Deep-link into SundayBuilder for the first Sunday of that season.
    // SundayBuilder's manual-date path handles arbitrary Sunday ISOs.
    if (!startsIso) { navigate("/worship-planner"); return; }
    // Season "start" may fall on a weekday (Ash Wednesday, Christmas
    // Day, etc.). Ask SundayBuilder to pick the first Sunday on or
    // after that start date by advancing the date-picker to the next
    // Sunday. We simply pass the ISO — the SundayBuilder resolver
    // validates and rejects non-Sundays with an inline error.
    const d = new Date(startsIso + "T12:00:00Z");
    // Snap forward to the next Sunday if this date is not a Sunday.
    const dow = d.getUTCDay();
    if (dow !== 0) {
      const daysForward = (7 - dow) % 7 || 7;
      d.setUTCDate(d.getUTCDate() + daysForward);
    }
    const iso = d.toISOString().slice(0, 10);
    navigate(`/worship-planner?date=${iso}&from=annual&season=${seasonKey}`);
  };

  const primaryCta = (milestone) => {
    if (!milestone) return null;
    // One primary action per screen — always "Begin planning".
    return (
      <button
        className="sb-btn primary"
        onClick={() => handleBeginPlanning(milestone.season_key, milestone.starts_iso)}
        data-testid="ap-begin-planning"
      >
        Begin planning
        <ChevronRight size={18} strokeWidth={2.5} />
      </button>
    );
  };

  return (
    <div className="sb-workspace" data-testid="annual-planner-page">
      <div className="sb-topbar" data-testid="ap-topbar">
        <div className="sb-brand">Annual<em>Planner</em>™</div>
        <button className="sb-back" onClick={() => navigate("/dashboard")} data-testid="ap-back">
          ← Dashboard
        </button>
      </div>

      {/* Altar frontal band — appears once, architectural */}
      <div className="sb-altar-hero" role="img" aria-label="Bas-relief altar frontal">
        <div className="sb-altar-hero__body">
          <div className="sb-altar-hero__brand">
            <img
              className="sb-altar-hero__monogram"
              src={`${process.env.PUBLIC_URL || ""}/brand/li-monogram.png`}
              alt=""
              aria-hidden="true"
              onError={(e) => { e.target.style.display = "none"; }}
            />
            <span className="sb-altar-hero__title">Annual Planner™</span>
          </div>
          <span className="sb-altar-hero__wordmark">Strategic Planning Desk</span>
        </div>
      </div>

      <div className="sb-screen">
        {err && (
          <div className="sb-incomplete" data-testid="ap-unavailable-card">
            <h2 className="sb-incomplete__title">Preparation is not currently available</h2>
            <p className="sb-incomplete__body">{err}</p>
          </div>
        )}

        {overview && !err && (
          <>
            {overview.today?.label && (
              <p className="sb-today" data-testid="ap-today-line">
                {overview.today.label}
                {overview.liturgical_year && (
                  <> · Liturgical Year {overview.liturgical_year.letter} ({_yearRangeLabel(overview.liturgical_year)})</>
                )}
              </p>
            )}
            <h1 className="sb-h1" data-testid="ap-heading">Your next planning milestone</h1>

            {/* Milestone card — one primary action */}
            {overview.milestone ? (
              <div className="ap-milestone" data-testid="ap-milestone-card">
                <p className="ap-milestone__reason" data-testid="ap-milestone-reason">
                  {overview.milestone.reason}
                </p>
                {overview.milestone.kind === "upcoming" && overview.milestone.starts_iso && (
                  <p className="ap-milestone__meta">
                    Begins {_monthDay(overview.milestone.starts_iso)}
                  </p>
                )}
                <hr className="sb-proposition__rule" />
                <div className="ap-milestone__actions">
                  {primaryCta(overview.milestone)}
                </div>
              </div>
            ) : (
              <div className="sb-incomplete" data-testid="ap-milestone-empty">
                <h2 className="sb-incomplete__title">The liturgical year is fully mapped</h2>
                <p className="sb-incomplete__body">
                  There are no more season boundaries in this liturgical year. Select any
                  season below to prepare within it.
                </p>
              </div>
            )}

            {/* Liturgical timeline */}
            <p className="ap-timeline__heading">The liturgical year</p>
            <ul className="ap-timeline" data-testid="ap-timeline">
              {(overview.timeline || []).map((s) => (
                <li key={s.key} className={`ap-timeline__row ap-timeline__row--${s.status}`}>
                  <button
                    className="ap-timeline__link"
                    onClick={() => handleBeginPlanning(s.key, s.starts_iso)}
                    data-testid={`ap-season-${s.key}`}
                    aria-label={`${s.name} · ${_statusLabel(s.status)}`}
                  >
                    <span className="ap-timeline__marker">{_statusIcon(s.status)}</span>
                    <span className="ap-timeline__name">{s.name}</span>
                    <span className="ap-timeline__dates">
                      {_monthDay(s.starts_iso)} – {_monthDay(s.ends_iso)}
                    </span>
                    <span className="ap-timeline__count">
                      {s.sunday_count} {s.sunday_count === 1 ? "Sunday" : "Sundays"}
                    </span>
                    <span className={`ap-timeline__status ap-timeline__status--${s.status}`}>
                      {_statusLabel(s.status)}
                    </span>
                    <ChevronRight size={16} strokeWidth={2} className="ap-timeline__chev" aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>

            {/* Institutional memory — only rendered when items exist (Rule 0) */}
            {Array.isArray(overview.memory) && overview.memory.length > 0 && (
              <>
                <p className="ap-timeline__heading">From your parish's memory</p>
                <ul className="ap-memory" data-testid="ap-memory">
                  {overview.memory.map((m, i) => (
                    <li key={i} className="ap-memory__row">
                      <span className="ap-memory__title">{m.title}</span>
                      {m.detail && <span className="ap-memory__detail">{m.detail}</span>}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
